import React, { useState, useEffect, useCallback } from "react";
import {
  Users, UserPlus, Clock, CheckCircle, XCircle, Send,
  MessageSquare, Lightbulb, ThumbsUp, Award, BookOpen,
  FileText, GraduationCap, Plus, Trash2, ChevronRight,
  Loader2, AlertTriangle, Shield, Star
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════
// TEAM HUB: Availability + Q&A + Suggestions + Performance
// ═══════════════════════════════════════════════════════════════

// ─── Availability Panel ───
const AvailabilityPanel = ({ authAxios }) => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markName, setMarkName] = useState("");
  const [markStatus, setMarkStatus] = useState("off");
  const [markReason, setMarkReason] = useState("");

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/doozybot/availability/today`);
      setTeam(r.data.team || []);
    } catch { }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleMark = async () => {
    if (!markName) return;
    try {
      await authAxios.post(`${API_URL}/api/doozybot/availability`, {
        member_name: markName, status: markStatus, reason: markReason,
      });
      setMarkName(""); setMarkReason("");
      fetch();
    } catch { }
  };

  const available = team.filter(t => t.status === "available");
  const unavailable = team.filter(t => t.status !== "available");

  return (
    <div data-testid="availability-panel">
      <h3 className="text-sm font-bold text-[#1E1B4B] mb-3 flex items-center gap-2"><Clock size={14} className="text-[#E83E8C]" /> Today's Availability</h3>

      {/* Quick mark */}
      <div className="bg-white rounded-xl border border-gray-100 p-3 mb-3 space-y-2">
        <p className="text-[10px] font-semibold text-[#94a3b8] uppercase">Mark Leave/Off</p>
        <div className="flex gap-2">
          <input value={markName} onChange={e => setMarkName(e.target.value)} placeholder="Member name"
            className="flex-1 px-3 py-1.5 rounded-lg border text-xs" data-testid="avail-name-input" />
          <select value={markStatus} onChange={e => setMarkStatus(e.target.value)}
            className="px-2 py-1.5 rounded-lg border text-xs" data-testid="avail-status-select">
            <option value="off">Day Off</option>
            <option value="leave">On Leave</option>
            <option value="half_day">Half Day</option>
          </select>
        </div>
        <div className="flex gap-2">
          <input value={markReason} onChange={e => setMarkReason(e.target.value)} placeholder="Reason (optional)"
            className="flex-1 px-3 py-1.5 rounded-lg border text-xs" />
          <button onClick={handleMark} disabled={!markName}
            className="px-3 py-1.5 rounded-lg bg-[#E83E8C] text-white text-xs font-medium disabled:opacity-40" data-testid="avail-mark-btn">Mark</button>
        </div>
      </div>

      {loading ? <Loader2 className="animate-spin text-[#E83E8C] mx-auto" size={20} /> : (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-bold text-emerald-600">{available.length} Available</span>
            {unavailable.length > 0 && <span className="text-xs font-bold text-red-500">{unavailable.length} Off/Leave</span>}
          </div>
          {unavailable.map((t, i) => (
            <div key={t.name || `u-${i}`} className="flex items-center gap-2 p-2 rounded-lg bg-red-50 border border-red-100">
              <XCircle size={14} className="text-red-400" />
              <span className="text-xs font-medium text-red-700">{t.name}</span>
              <span className="text-[10px] text-red-500 ml-auto">{t.status} {t.reason ? `— ${t.reason}` : ""}</span>
            </div>
          ))}
          {available.map((t, i) => (
            <div key={t.name || `a-${i}`} className="flex items-center gap-2 p-2 rounded-lg bg-white border border-gray-100">
              <CheckCircle size={14} className="text-emerald-400" />
              <span className="text-xs text-[#1E1B4B]">{t.name}</span>
              <span className="text-[10px] text-[#94a3b8] ml-auto">{t.role}</span>
            </div>
          ))}
          {team.length === 0 && <p className="text-xs text-[#94a3b8] text-center py-4">No team members found</p>}
        </div>
      )}
    </div>
  );
};

// ─── Team Q&A Panel ───
const TeamQAPanel = ({ authAxios }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState("");
  const [askedBy, setAskedBy] = useState("");
  const [asking, setAsking] = useState(false);
  const [lastAnswer, setLastAnswer] = useState(null);
  const [respondId, setRespondId] = useState(null);
  const [respondText, setRespondText] = useState("");

  const fetchQ = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/doozybot/team-questions`);
      setQuestions(r.data.questions || []);
    } catch { }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchQ(); }, [fetchQ]);

  const handleAsk = async () => {
    if (!question.trim()) return;
    setAsking(true); setLastAnswer(null);
    try {
      const r = await authAxios.post(`${API_URL}/api/doozybot/team-chat`, {
        question: question.trim(), asked_by: askedBy || "team_member",
      });
      setLastAnswer(r.data);
      setQuestion("");
      fetchQ();
    } catch { }
    setAsking(false);
  };

  const handleRespond = async (qaId) => {
    if (!respondText.trim()) return;
    try {
      await authAxios.post(`${API_URL}/api/doozybot/team-questions/${qaId}/respond`, { response: respondText });
      setRespondId(null); setRespondText("");
      fetchQ();
    } catch { }
  };

  const routed = questions.filter(q => q.routed_to_admin);

  return (
    <div data-testid="team-qa-panel">
      <h3 className="text-sm font-bold text-[#1E1B4B] mb-3 flex items-center gap-2"><MessageSquare size={14} className="text-[#E83E8C]" /> Team Q&A</h3>

      {/* Ask a question */}
      <div className="bg-white rounded-xl border border-gray-100 p-3 mb-3 space-y-2">
        <input value={askedBy} onChange={e => setAskedBy(e.target.value)} placeholder="Your name"
          className="w-full px-3 py-1.5 rounded-lg border text-xs" data-testid="qa-name-input" />
        <div className="flex gap-2">
          <input value={question} onChange={e => setQuestion(e.target.value)} placeholder="Ask DoozyBot anything..."
            onKeyDown={e => e.key === "Enter" && handleAsk()}
            className="flex-1 px-3 py-1.5 rounded-lg border text-xs" data-testid="qa-question-input" />
          <button onClick={handleAsk} disabled={asking || !question.trim()}
            className="px-3 py-1.5 rounded-lg bg-[#E83E8C] text-white text-xs font-medium disabled:opacity-40" data-testid="qa-ask-btn">
            {asking ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
          </button>
        </div>
      </div>

      {/* Last answer */}
      {lastAnswer && (
        <div className="bg-indigo-50 rounded-xl border border-indigo-100 p-3 mb-3">
          <p className="text-xs font-medium text-indigo-800 mb-1">DoozyBot says:</p>
          <p className="text-xs text-indigo-700 whitespace-pre-wrap">{lastAnswer.answer}</p>
          {lastAnswer.routed_to_admin && (
            <p className="text-[10px] text-amber-600 mt-2 font-medium">This question has been routed to your manager for a detailed response.</p>
          )}
        </div>
      )}

      {/* Routed questions needing admin response */}
      {routed.length > 0 && (
        <div className="mb-3">
          <p className="text-[10px] font-bold text-amber-600 uppercase mb-2 flex items-center gap-1">
            <AlertTriangle size={10} /> {routed.length} question(s) need your response
          </p>
          {routed.map(q => (
            <div key={q.qa_id} className="bg-amber-50 rounded-xl border border-amber-200 p-3 mb-2">
              <p className="text-xs font-medium text-[#1E1B4B]">Q: {q.question}</p>
              <p className="text-[10px] text-[#94a3b8]">Asked by {q.asked_by} — {q.created_at?.slice(0, 16)}</p>
              {respondId === q.qa_id ? (
                <div className="flex gap-2 mt-2">
                  <input value={respondText} onChange={e => setRespondText(e.target.value)} placeholder="Your response..."
                    className="flex-1 px-2 py-1 rounded-lg border text-xs" />
                  <button onClick={() => handleRespond(q.qa_id)} className="px-2 py-1 rounded-lg bg-emerald-500 text-white text-[10px]">Send</button>
                </div>
              ) : (
                <button onClick={() => setRespondId(q.qa_id)} className="mt-2 text-[10px] text-[#E83E8C] font-medium hover:underline">Respond</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Suggestions Panel ───
const SuggestionsPanel = ({ authAxios }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", submitted_by: "", category: "general" });

  const fetchS = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/doozybot/suggestions`);
      setSuggestions(r.data.suggestions || []);
    } catch { }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchS(); }, [fetchS]);

  const handleSubmit = async () => {
    if (!form.title.trim()) return;
    try {
      await authAxios.post(`${API_URL}/api/doozybot/suggestions`, form);
      setShowAdd(false); setForm({ title: "", description: "", submitted_by: "", category: "general" });
      fetchS();
    } catch { }
  };

  const handleStatusUpdate = async (sugId, status) => {
    try {
      await authAxios.put(`${API_URL}/api/doozybot/suggestions/${sugId}`, { status });
      fetchS();
    } catch { }
  };

  const statusColors = {
    new: "bg-blue-100 text-blue-700",
    reviewed: "bg-amber-100 text-amber-700",
    approved: "bg-emerald-100 text-emerald-700",
    implemented: "bg-purple-100 text-purple-700",
    rejected: "bg-gray-100 text-gray-500",
  };

  return (
    <div data-testid="suggestions-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2"><Lightbulb size={14} className="text-amber-500" /> Ideas & Suggestions</h3>
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-[#E83E8C] text-white text-[10px] font-medium" data-testid="add-suggestion-btn">
          <Plus size={10} /> New Idea
        </button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-xl border border-[#E83E8C]/20 p-3 mb-3 space-y-2" data-testid="suggestion-form">
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Idea title"
            className="w-full px-3 py-1.5 rounded-lg border text-xs" data-testid="sug-title-input" />
          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe your idea..."
            rows={2} className="w-full px-3 py-1.5 rounded-lg border text-xs" />
          <div className="flex gap-2">
            <input value={form.submitted_by} onChange={e => setForm(f => ({ ...f, submitted_by: e.target.value }))} placeholder="Your name"
              className="flex-1 px-3 py-1.5 rounded-lg border text-xs" />
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              className="px-2 py-1.5 rounded-lg border text-xs">
              <option value="general">General</option>
              <option value="product">Product</option>
              <option value="process">Process</option>
              <option value="marketing">Marketing</option>
              <option value="tech">Tech</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSubmit} className="px-3 py-1.5 rounded-lg bg-[#E83E8C] text-white text-xs font-medium" data-testid="sug-submit-btn">Submit</button>
            <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 rounded-lg border text-xs">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <Loader2 className="animate-spin text-[#E83E8C] mx-auto" size={20} /> : (
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {suggestions.map(s => (
            <div key={s.suggestion_id} className="bg-white rounded-xl border border-gray-100 p-3" data-testid={`suggestion-${s.suggestion_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-medium text-[#1E1B4B]">{s.title}</p>
                  {s.description && <p className="text-[10px] text-[#94a3b8] mt-0.5">{s.description}</p>}
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${statusColors[s.status] || statusColors.new}`}>{s.status}</span>
                    <span className="text-[9px] text-[#94a3b8]">by {s.submitted_by || "anonymous"} — {s.category}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  {s.status === "new" && (
                    <>
                      <button onClick={() => handleStatusUpdate(s.suggestion_id, "approved")} className="p-1 rounded hover:bg-emerald-50" title="Approve"><CheckCircle size={12} className="text-emerald-500" /></button>
                      <button onClick={() => handleStatusUpdate(s.suggestion_id, "rejected")} className="p-1 rounded hover:bg-red-50" title="Reject"><XCircle size={12} className="text-red-400" /></button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
          {suggestions.length === 0 && <p className="text-xs text-[#94a3b8] text-center py-4">No suggestions yet. Be the first!</p>}
        </div>
      )}
    </div>
  );
};

// ─── Performance Panel ───
const PerformancePanel = ({ authAxios }) => {
  const [perf, setPerf] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authAxios.get(`${API_URL}/api/doozybot/team-performance`)
      .then(r => setPerf(r.data.performance || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authAxios]);

  return (
    <div data-testid="performance-panel">
      <h3 className="text-sm font-bold text-[#1E1B4B] mb-3 flex items-center gap-2"><Award size={14} className="text-amber-500" /> Team Performance (30d)</h3>
      {loading ? <Loader2 className="animate-spin text-[#E83E8C] mx-auto" size={20} /> : (
        <div className="space-y-2">
          {perf.map((p, i) => (
            <div key={p.name || `perf-${i}`} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                i === 0 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500"
              }`}>#{i + 1}</div>
              <div className="flex-1">
                <p className="text-xs font-medium text-[#1E1B4B]">{p.name}</p>
                <p className="text-[10px] text-[#94a3b8]">{p.role}</p>
              </div>
              <div className="text-right">
                <p className="text-xs font-bold text-[#E83E8C]">{p.bookings_handled} bookings</p>
                <p className="text-[10px] text-[#94a3b8]">{p.leads_contacted} leads</p>
              </div>
            </div>
          ))}
          {perf.length === 0 && <p className="text-xs text-[#94a3b8] text-center py-4">No performance data yet</p>}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// TEAM HUB — Combined tab
// ═══════════════════════════════════════════════════════════════
export const TeamHubPanel = ({ authAxios }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    <AvailabilityPanel authAxios={authAxios} />
    <TeamQAPanel authAxios={authAxios} />
    <SuggestionsPanel authAxios={authAxios} />
    <PerformancePanel authAxios={authAxios} />
  </div>
);


// ═══════════════════════════════════════════════════════════════
// TRAINING & ONBOARDING
// ═══════════════════════════════════════════════════════════════

// ─── Training Documents ───
const TrainingDocsPanel = ({ authAxios }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", category: "general", role_target: "all" });

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/doozybot/training/documents`);
      setDocs(r.data.documents || []);
    } catch { }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleAdd = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    try {
      await authAxios.post(`${API_URL}/api/doozybot/training/documents`, form);
      setShowAdd(false); setForm({ title: "", content: "", category: "general", role_target: "all" });
      fetchDocs();
    } catch { }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await authAxios.delete(`${API_URL}/api/doozybot/training/documents/${docId}`);
      fetchDocs();
    } catch { }
  };

  const catColors = { general: "bg-gray-100 text-gray-600", sop: "bg-blue-100 text-blue-700", policy: "bg-red-100 text-red-700", guide: "bg-emerald-100 text-emerald-700", process: "bg-purple-100 text-purple-700" };

  return (
    <div data-testid="training-docs-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2"><FileText size={14} className="text-[#E83E8C]" /> Training Documents ({docs.length})</h3>
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-[#E83E8C] text-white text-[10px] font-medium" data-testid="add-doc-btn">
          <Plus size={10} /> Add Document
        </button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-xl border border-[#E83E8C]/20 p-3 mb-3 space-y-2" data-testid="training-doc-form">
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Document title"
            className="w-full px-3 py-1.5 rounded-lg border text-xs" data-testid="doc-title-input" />
          <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="Document content (SOPs, policies, guides...)"
            rows={5} className="w-full px-3 py-1.5 rounded-lg border text-xs" data-testid="doc-content-input" />
          <div className="flex gap-2">
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="px-2 py-1.5 rounded-lg border text-xs">
              <option value="general">General</option>
              <option value="sop">SOP</option>
              <option value="policy">Policy</option>
              <option value="guide">Guide</option>
              <option value="process">Process</option>
            </select>
            <select value={form.role_target} onChange={e => setForm(f => ({ ...f, role_target: e.target.value }))} className="px-2 py-1.5 rounded-lg border text-xs">
              <option value="all">All Roles</option>
              <option value="ops">Ops/Decorators</option>
              <option value="sales">Sales</option>
              <option value="manager">Managers</option>
            </select>
            <button onClick={handleAdd} className="px-3 py-1.5 rounded-lg bg-[#E83E8C] text-white text-xs font-medium" data-testid="doc-save-btn">Save</button>
            <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 rounded-lg border text-xs">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <Loader2 className="animate-spin text-[#E83E8C] mx-auto" size={20} /> : (
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {docs.map(d => (
            <div key={d.doc_id} className="bg-white rounded-xl border border-gray-100 p-3" data-testid={`doc-${d.doc_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${catColors[d.category] || catColors.general}`}>{d.category}</span>
                    <span className="text-[9px] text-[#94a3b8]">For: {d.role_target}</span>
                  </div>
                  <p className="text-xs font-medium text-[#1E1B4B]">{d.title}</p>
                  <p className="text-[10px] text-[#94a3b8] mt-0.5 line-clamp-2">{d.content}</p>
                </div>
                <button onClick={() => handleDelete(d.doc_id)} className="p-1 rounded hover:bg-red-50"><Trash2 size={12} className="text-red-400" /></button>
              </div>
            </div>
          ))}
          {docs.length === 0 && <p className="text-xs text-[#94a3b8] text-center py-4">No training documents yet. Add your first!</p>}
        </div>
      )}
    </div>
  );
};

// ─── Onboarding Panel ───
const OnboardingPanel = ({ authAxios }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboard, setShowOnboard] = useState(false);
  const [form, setForm] = useState({ member_name: "", member_role: "", department: "" });
  const [onboarding, setOnboarding] = useState(false);
  const [lastPlan, setLastPlan] = useState(null);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/doozybot/training/plans`);
      setPlans(r.data.plans || []);
    } catch { }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const handleOnboard = async () => {
    if (!form.member_name.trim() || !form.member_role.trim()) return;
    setOnboarding(true); setLastPlan(null);
    try {
      const r = await authAxios.post(`${API_URL}/api/doozybot/training/onboard`, form);
      setLastPlan(r.data);
      setShowOnboard(false); setForm({ member_name: "", member_role: "", department: "" });
      fetchPlans();
    } catch { }
    setOnboarding(false);
  };

  return (
    <div data-testid="onboarding-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2"><UserPlus size={14} className="text-emerald-500" /> Onboarding</h3>
        <button onClick={() => setShowOnboard(true)} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-500 text-white text-[10px] font-medium" data-testid="onboard-new-btn">
          <UserPlus size={10} /> New Joiner
        </button>
      </div>

      {showOnboard && (
        <div className="bg-white rounded-xl border border-emerald-200 p-3 mb-3 space-y-2" data-testid="onboard-form">
          <input value={form.member_name} onChange={e => setForm(f => ({ ...f, member_name: e.target.value }))} placeholder="New member's name"
            className="w-full px-3 py-1.5 rounded-lg border text-xs" data-testid="onboard-name-input" />
          <div className="flex gap-2">
            <select value={form.member_role} onChange={e => setForm(f => ({ ...f, member_role: e.target.value }))}
              className="flex-1 px-2 py-1.5 rounded-lg border text-xs" data-testid="onboard-role-select">
              <option value="">Select Role</option>
              <option value="ops">Decorator / Ops</option>
              <option value="sales">Sales Executive</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </select>
            <input value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} placeholder="Department"
              className="flex-1 px-3 py-1.5 rounded-lg border text-xs" />
          </div>
          <div className="flex gap-2">
            <button onClick={handleOnboard} disabled={onboarding || !form.member_name || !form.member_role}
              className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-medium disabled:opacity-40 flex items-center gap-1" data-testid="onboard-create-btn">
              {onboarding ? <Loader2 size={12} className="animate-spin" /> : <GraduationCap size={12} />}
              {onboarding ? "Creating plan..." : "Create Training Plan"}
            </button>
            <button onClick={() => setShowOnboard(false)} className="px-3 py-1.5 rounded-lg border text-xs">Cancel</button>
          </div>
        </div>
      )}

      {/* Last created plan */}
      {lastPlan && (
        <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 mb-3" data-testid="onboard-plan-result">
          <p className="text-sm font-bold text-emerald-800 mb-2">Training Plan Created!</p>
          <p className="text-xs text-emerald-700 mb-3">{lastPlan.welcome_message}</p>
          {lastPlan.training_steps?.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-bold text-emerald-600 uppercase">Training Steps:</p>
              {lastPlan.training_steps.map((step, i) => (
                <div key={(step.title || step.step || "") + "-" + i} className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-200 text-emerald-800 flex items-center justify-center text-[9px] font-bold">{i + 1}</span>
                  <div>
                    <p className="text-xs font-medium text-[#1E1B4B]">{step.title || step.step || `Step ${i + 1}`}</p>
                    {step.description && <p className="text-[10px] text-[#94a3b8]">{step.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {lastPlan.first_week_goals?.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] font-bold text-emerald-600 uppercase mb-1">First Week Goals:</p>
              {lastPlan.first_week_goals.map((g, i) => (
                <p key={`goal-${g}-${i}`} className="text-xs text-emerald-700 flex items-center gap-1"><ChevronRight size={10} />{g}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Existing plans */}
      {loading ? <Loader2 className="animate-spin text-[#E83E8C] mx-auto" size={20} /> : (
        <div className="space-y-2">
          {plans.map(p => (
            <div key={p.plan_id} className="bg-white rounded-xl border border-gray-100 p-3" data-testid={`plan-${p.plan_id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-[#1E1B4B]">{p.member_name}</p>
                  <p className="text-[10px] text-[#94a3b8]">{p.member_role} — {p.department || "General"}</p>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${p.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{p.status}</span>
                  <p className="text-[9px] text-[#94a3b8] mt-0.5">{p.training_steps?.length || 0} steps</p>
                </div>
              </div>
            </div>
          ))}
          {plans.length === 0 && !lastPlan && <p className="text-xs text-[#94a3b8] text-center py-4">No onboarding plans yet</p>}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// TRAINING HUB — Combined tab
// ═══════════════════════════════════════════════════════════════
export const TrainingHubPanel = ({ authAxios }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    <OnboardingPanel authAxios={authAxios} />
    <TrainingDocsPanel authAxios={authAxios} />
  </div>
);

// ═══════════════════════════════════════════════════════════════
// ESCALATIONS PANEL
// ═══════════════════════════════════════════════════════════════
export const EscalationsPanel = ({ authAxios }) => {
  const [escalations, setEscalations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEsc = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/doozybot/escalations`);
      setEscalations(r.data.escalations || []);
    } catch { }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchEsc(); }, [fetchEsc]);

  const handleResolve = async (escId) => {
    try {
      await authAxios.post(`${API_URL}/api/doozybot/escalations/resolve`, { escalation_id: escId });
      fetchEsc();
    } catch { }
  };

  const levelLabels = { 1: "L1 — Team Nudge", 2: "L2 — Manager Alert", 3: "L3 — Super Admin" };
  const levelColors = { 1: "bg-amber-100 text-amber-700", 2: "bg-orange-100 text-orange-700", 3: "bg-red-100 text-red-700" };

  return (
    <div data-testid="escalations-panel">
      <h3 className="text-sm font-bold text-[#1E1B4B] mb-3 flex items-center gap-2"><Shield size={14} className="text-red-500" /> Escalation Chain ({escalations.length})</h3>
      {loading ? <Loader2 className="animate-spin text-[#E83E8C] mx-auto" size={20} /> : (
        <div className="space-y-2">
          {escalations.map(e => (
            <div key={e.escalation_id} className={`p-3 rounded-xl border ${e.level >= 3 ? "border-red-300 bg-red-50" : e.level >= 2 ? "border-orange-200 bg-orange-50" : "border-amber-200 bg-amber-50"}`} data-testid={`esc-${e.escalation_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${levelColors[e.level] || levelColors[1]}`}>{levelLabels[e.level] || `L${e.level}`}</span>
                    <span className="text-[9px] text-gray-500">{e.department}</span>
                  </div>
                  <p className="text-xs font-medium text-[#1E1B4B]">{e.alert_title}</p>
                  <p className="text-[10px] text-[#94a3b8] mt-0.5">{e.alert_detail}</p>
                </div>
                <button onClick={() => handleResolve(e.escalation_id)} className="flex-shrink-0 px-2 py-1 rounded-lg bg-emerald-500 text-white text-[10px] font-medium" data-testid={`resolve-esc-${e.escalation_id}`}>
                  Resolve
                </button>
              </div>
            </div>
          ))}
          {escalations.length === 0 && (
            <div className="text-center py-6 bg-emerald-50 rounded-xl border border-emerald-200">
              <CheckCircle size={24} className="text-emerald-500 mx-auto mb-1" />
              <p className="text-xs font-semibold text-emerald-700">No Active Escalations</p>
              <p className="text-[10px] text-emerald-600/70">All alerts are acknowledged or resolved</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
